import { useEffect } from 'react';
import { create } from 'zustand';
import {
  getSavedPrinter,
  getAuthorizedBluetoothDevice,
  getPrinterSettings,
  savePrinterSettings,
  savePrinter,
  type SavedPrinter,
  type PrinterSettings,
} from 'utils/printerUtils';

interface PrinterStore {
  activeDevice: USBDevice | BluetoothDevice | null;
  savedPrinter: SavedPrinter | null;
  printerSettings: PrinterSettings;
  setActiveDevice: (device: USBDevice | BluetoothDevice | null) => void;
  setSavedPrinter: (printer: SavedPrinter | null) => void;
  updatePrinterSettings: (settings: Partial<PrinterSettings>) => void;
}

export const usePrinterStore = create<PrinterStore>((set, get) => ({
  activeDevice: null,
  savedPrinter: null,
  printerSettings: { enabled: true, autoCut: true },

  setActiveDevice: (device) => set({ activeDevice: device }),

  setSavedPrinter: (printer) => {
    set({ savedPrinter: printer });
    if (printer) {
      savePrinter(printer);
    } else {
      localStorage.removeItem('selected_printer');
    }
  },

  updatePrinterSettings: (newSettings) => {
    const updated = { ...get().printerSettings, ...newSettings };
    savePrinterSettings(updated);
    set({ printerSettings: updated });
  },
}));

export function usePrinterInit(): void {
  const setSavedPrinter = usePrinterStore((s) => s.setSavedPrinter);
  const setActiveDevice = usePrinterStore((s) => s.setActiveDevice);
  const updatePrinterSettings = usePrinterStore((s) => s.updatePrinterSettings);

  useEffect(() => {
    const loadPrinter = async () => {
      const saved = getSavedPrinter();
      if (saved) {
        setSavedPrinter(saved);
        if (saved.type === 'bluetooth') {
          const device = await getAuthorizedBluetoothDevice(saved.id);
          if (device) {
            setActiveDevice(device);
          }
        } else if (saved.type === 'usb') {
          const [vendorId, productId] = saved.id.split('_').map(Number);
          if (navigator.usb) {
            try {
              const devices = await navigator.usb.getDevices();
              const device =
                devices.find(
                  (d) => d.vendorId === vendorId && d.productId === productId,
                ) || null;
              if (device) {
                setActiveDevice(device);
                try {
                  if (!device.opened) {
                    await device.open();
                  }
                  if (device.configuration === null) {
                    await device.selectConfiguration(1);
                  }
                  try {
                    await device.claimInterface(0);
                  } catch {
                    console.log('USB interface already claimed or unavailable');
                  }
                } catch (err) {
                  console.warn('Failed to open USB device on reconnect:', err);
                }
              }
            } catch (err) {
              console.warn('Failed to get USB devices:', err);
            }
          }
        }
      }
      const settings = getPrinterSettings();
      updatePrinterSettings(settings);
    };

    void loadPrinter();
  }, [setSavedPrinter, setActiveDevice, updatePrinterSettings]);
}
